import {
  Button,
  DetailList,
  formatDate,
  formatPhone,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  type SearchResult,
  SearchSelect,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@majitap/ui/arc/tabs/tabs";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { api, errorOf } from "../../api";
import { type Business, businessActions, businessQuery, type MemberRole, ROLES } from "../../businesses";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { ACQUISITION_SOURCE } from "../../labels";
import { useDebounced } from "../../search";
import { meQuery } from "../../session";
import styles from "../customers/customers.module.css";
import { PlacesPanel } from "../customers/places";
import { useStaffName } from "../shared/acquisition-fields";
import { ConfirmDialog } from "../shared/confirm";
import { InvoicesPanel, PaymentsPanel } from "../shared/finance";
import { IntegrationLinks } from "../shared/integration-links";
import { ActivityPanel, CreditPanel, OrdersPanel, WalletPanel } from "../shared/panels";
import { QuickbooksStatus } from "../shared/sync-status";
import { BusinessDialog } from "./list";
import { BusinessPrices } from "./prices";

export const BUSINESS_TABS = [
  "overview",
  "members",
  "places",
  "prices",
  "orders",
  "invoices",
  "payments",
  "wallet",
  "credit",
  "activity",
] as const;
type BusinessTab = (typeof BUSINESS_TABS)[number];

/** One business (P8-11): its details and branches, members, places, prices, orders, wallet, credit and history. */
export function BusinessPage(): ReactNode {
  const { businessId } = useParams({ from: "/console/businesses/$businessId" });
  const search = useSearch({ from: "/console/businesses/$businessId" });
  const navigate = useNavigate({ from: "/businesses/$businessId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const business = useQuery(businessQuery(businessId));
  const [dialog, setDialog] = useState<"edit" | "status" | "delete" | null>(null);
  useRecordCrumb(business.data?.business_name);

  if (business.status === "pending") return <PageLoading label="Loading the business" />;
  if (business.status === "error") {
    const error = errorOf(business.error);
    if (error.code === "BUSINESS_NOT_FOUND")
      return <PageEmpty title="No such business" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => business.refetch()} />;
  }
  const b = business.data;
  const canManage = permissions.includes("customers.manage");
  const canOrder = permissions.includes("orders.write");
  const tabs: { id: BusinessTab; label: string; shown: boolean }[] = [
    { id: "overview", label: "Overview", shown: true },
    { id: "members", label: "Members", shown: true },
    { id: "places", label: "Places and tanks", shown: true },
    { id: "prices", label: "Prices", shown: permissions.includes("orders.read") },
    { id: "orders", label: "Orders", shown: permissions.includes("orders.read") },
    { id: "wallet", label: "Wallet", shown: permissions.includes("payments.record") },
    { id: "invoices", label: "Invoices", shown: permissions.includes("invoices.issue") },
    { id: "payments", label: "Payments", shown: permissions.includes("payments.record") },
    { id: "credit", label: "Credit", shown: permissions.includes("invoices.issue") },
    { id: "activity", label: "Activity", shown: permissions.includes("staff.manage") },
  ];
  const visible = tabs.filter((t) => t.shown);
  const tab = visible.some((t) => t.id === search.tab) ? (search.tab as BusinessTab) : "overview";

  return (
    <>
      <PageHeader
        title={b.business_name}
        description={[
          b.tax_pin ? `Tax PIN ${b.tax_pin}` : null,
          b.members.length === 1 ? "1 member" : `${b.members.length} members`,
          b.parent ? `branch of ${b.parent.business_name}` : null,
          b.is_active ? null : "Inactive",
        ]
          .filter(Boolean)
          .join(" · ")}
        actions={
          canManage || canOrder ? (
            <>
              {canOrder && b.is_active ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate({ to: "/orders/new", search: { business_id: b.id } })}
                >
                  New order
                </Button>
              ) : null}
              {canManage ? (
                <>
                  <Button variant="secondary" onClick={() => setDialog("delete")}>
                    Delete
                  </Button>
                  <Button variant="secondary" onClick={() => setDialog("status")}>
                    {b.is_active ? "Deactivate" : "Reactivate"}
                  </Button>
                  <Button onClick={() => setDialog("edit")}>Edit</Button>
                </>
              ) : null}
            </>
          ) : undefined
        }
      />
      <Tabs
        value={tab}
        onValueChange={(v) =>
          navigate({ search: { tab: v === "overview" ? undefined : (v as BusinessTab) }, replace: true })
        }
      >
        <TabsList aria-label="About this business">
          {visible.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <Overview business={b} />
          <QuickbooksStatus entityType="business" entityId={b.id} />
          <IntegrationLinks owner={{ business_id: b.id }} />
        </TabsContent>
        <TabsContent value="members">
          <Members business={b} canManage={canManage} />
        </TabsContent>
        <TabsContent value="places">
          <PlacesPanel owner={{ business_id: b.id }} canEdit={canManage} />
        </TabsContent>
        <TabsContent value="prices">{tab === "prices" ? <BusinessPrices businessId={b.id} /> : null}</TabsContent>
        <TabsContent value="orders">
          {tab === "orders" ? <OrdersPanel owner={{ business_id: b.id }} /> : null}
        </TabsContent>
        <TabsContent value="invoices">
          {tab === "invoices" ? <InvoicesPanel owner={{ business_id: b.id }} /> : null}
        </TabsContent>
        <TabsContent value="payments">
          {tab === "payments" ? (
            <PaymentsPanel filter={{ business_id: b.id }} empty="Payments they make, and refunds to them, show here." />
          ) : null}
        </TabsContent>
        <TabsContent value="wallet">
          {tab === "wallet" ? <WalletPanel owner={{ business_id: b.id }} /> : null}
        </TabsContent>
        <TabsContent value="credit">
          {tab === "credit" ? (
            <CreditPanel owner={{ kind: "business", id: b.id }} paymentTermId={b.payment_term_id} />
          ) : null}
        </TabsContent>
        <TabsContent value="activity">
          {tab === "activity" ? (
            <ActivityPanel targetType="business" id={b.id} label="What changed for this business" />
          ) : null}
        </TabsContent>
      </Tabs>
      {dialog === "edit" ? <BusinessDialog business={b} onClose={() => setDialog(null)} /> : null}
      {dialog === "status" ? <ActiveDialog business={b} onClose={() => setDialog(null)} /> : null}
      {dialog === "delete" ? (
        <ConfirmDialog
          title={`Delete ${b.business_name}?`}
          description={
            b.branches.length
              ? "It has branches: make them businesses on their own, or delete them, first."
              : "For a duplicate or a test business. Its orders, invoices and history stay; it no longer shows in lists or searches. To pause one instead, deactivate it."
          }
          action="Delete"
          danger
          run={() => businessActions.remove(b.id)}
          onDone={() => navigate({ to: "/businesses", search: {} })}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

function Overview({ business: b }: { business: Business }) {
  const format = useCompanyFormat();
  const terms = useQuery(paymentTermsQuery);
  const manager = useStaffName(b.account_manager_staff_id);
  return (
    <div className={styles.facts}>
      <DetailList
        items={[
          { label: "Name", value: b.business_name },
          { label: "Tax PIN", value: b.tax_pin },
          { label: "Name on eTIMS invoices", value: b.tax_name },
          { label: "Email", value: b.email },
          { label: "Phone", value: formatPhone(b.phone) },
          {
            label: "Branch of",
            value: b.parent ? (
              <Link to="/businesses/$businessId" params={{ businessId: b.parent.id }} className={styles.link}>
                {b.parent.business_name}
              </Link>
            ) : null,
          },
          {
            label: "Branches",
            value: b.branches.length
              ? b.branches.map((br) => (
                  <div key={br.id}>
                    <Link to="/businesses/$businessId" params={{ businessId: br.id }} className={styles.link}>
                      {br.business_name}
                    </Link>
                  </div>
                ))
              : null,
          },
          {
            label: "Payment terms",
            value: terms.data?.find((t) => t.id === b.payment_term_id)?.name ?? "The company's default",
          },
          { label: "Added", value: formatDate(b.created_at, format) },
          { label: "How they were won", value: b.acquisition_source ? ACQUISITION_SOURCE[b.acquisition_source] : null },
          { label: "Account manager", value: manager },
        ]}
      />
    </div>
  );
}

/** Who orders for the business, and with what role. An owner manages the others from the portal too. */
function Members({ business: b, canManage }: { business: Business; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["business", b.id] });
  const change = useMutation({
    mutationFn: ({ customerId, role }: { customerId: string; role: MemberRole }) =>
      businessActions.setMember(b.id, customerId, role),
    onSuccess: refresh,
    onError: (e) => setProblem(messageFor(errorOf(e))),
  });
  const remove = useMutation({
    mutationFn: (customerId: string) => businessActions.removeMember(b.id, customerId),
    onSuccess: refresh,
    onError: (e) => setProblem(messageFor(errorOf(e))),
  });
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>Owners and managers see all of the business's orders; users see their own.</span>
        {canManage ? (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            Add a member
          </Button>
        ) : null}
      </div>
      {problem ? (
        <Alert tone="danger" title="That didn't work">
          {problem}
        </Alert>
      ) : null}
      <SortableDataTable
        caption="Members"
        rowKey="customer_id"
        rows={b.members}
        itemName={{ one: "member", other: "members" }}
        emptyMessage="No members yet"
        columns={[
          {
            key: "name",
            label: "Member",
            sortable: false,
            render: (_v, m) => (
              <Link to="/customers/$customerId" params={{ customerId: m.customer_id }} className={styles.link}>
                {m.name}
              </Link>
            ),
          },
          {
            key: "phone_number",
            label: "Phone",
            sortable: false,
            render: (_v, m) => formatPhone(m.phone_number) || "—",
          },
          {
            key: "role",
            label: "Role",
            sortable: false,
            render: (_v, m) =>
              canManage ? (
                <DropdownMenu
                  label={ROLES[m.role].label}
                  items={(Object.keys(ROLES) as MemberRole[])
                    .filter((role) => role !== m.role)
                    .map((role) => ({
                      label: `Make ${ROLES[role].label.toLowerCase()}`,
                      onSelect: () => change.mutate({ customerId: m.customer_id, role }),
                    }))}
                />
              ) : (
                <Badge>{ROLES[m.role].label}</Badge>
              ),
          },
          {
            key: "actions",
            label: "",
            sortable: false,
            // room for Remove to open into its confirm
            width: 240,
            render: (_v, m) =>
              canManage ? (
                <ConfirmMorph
                  label="Remove"
                  prompt="Remove?"
                  confirmLabel="Remove"
                  cancelLabel="Keep"
                  pendingLabel="Removing"
                  doneLabel="Removed"
                  errorLabel="Not removed"
                  tone="danger"
                  onConfirm={() => remove.mutateAsync(m.customer_id)}
                />
              ) : null,
          },
        ]}
      />
      {adding ? <AddMemberDialog business={b} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function AddMemberDialog({ business, onClose }: { business: Business; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [customer, setCustomer] = useState<SearchResult | null>(null);
  const [role, setRole] = useState<MemberRole>("user");
  const q = useDebounced(query.trim());
  const found = useQuery({
    queryKey: ["customer-search", q],
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/customers", { params: { query: { q, limit: 8 } } });
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  });
  const mutation = useMutation({
    mutationFn: () => businessActions.setMember(business.id, customer?.id ?? "", role),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business", business.id] });
      onClose();
    },
  });
  const already = new Set(business.members.map((m) => m.customer_id));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Add a member to ${business.business_name}`}
        description="Members are customers: add them under Customers first if they aren't one."
      >
        <div className={styles.form}>
          <SearchSelect
            label="Customer"
            placeholder="Search by name or phone number"
            value={customer}
            onChange={setCustomer}
            query={query}
            onQueryChange={setQuery}
            pending={query.trim() !== q || found.isFetching}
            results={(found.data ?? [])
              .filter((c) => !already.has(c.id))
              .map((c) => ({ id: c.id, label: c.user.name, description: formatPhone(c.user.phone_number) }))}
          />
          <Select
            label="Role"
            value={role}
            onValueChange={(v) => setRole(v as MemberRole)}
            options={Object.entries(ROLES).map(([value, r]) => ({ value, label: r.label }))}
            description={ROLES[role].help}
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not added">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!customer} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Add member
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ActiveDialog({ business, onClose }: { business: Business; onClose: () => void }) {
  const queryClient = useQueryClient();
  const deactivate = business.is_active;
  const mutation = useMutation({
    mutationFn: () => businessActions.update(business.id, { is_active: !deactivate }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business", business.id] });
      await queryClient.invalidateQueries({ queryKey: ["businesses"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={deactivate ? `Deactivate ${business.business_name}?` : `Reactivate ${business.business_name}?`}
        description={
          deactivate
            ? "Its members can't order for it until it's reactivated. Its orders, invoices and history stay."
            : "Its members can order for it again."
        }
      >
        <div className={styles.form}>
          {mutation.error ? (
            <Alert tone="danger" title="Nothing changed">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={deactivate ? "danger" : "primary"}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {deactivate ? "Deactivate" : "Reactivate"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
